import { observer } from "mobx-react-lite";
import { SortDropdownButton } from "@/components/filters/SortDropdownButton";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { useTabPageStore } from "@/hooks/useTabPageStore";
import styled from "@emotion/styled";
import {
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSText,
  MDSTextVariant,
  MDSIconButton,
  MDSPanelHeaderButtonGroup,
  MDSIconButtonSize,
} from "@/mds";
import { IndeterminateLoaderBar } from "@/components/loaders/IndeterminateLoaderBar";
import { ContactsFacetFilter } from "@/components/filters/ContactsFacetFilter";
import { CollectionsFacetFilter } from "@/components/filters/CollectionsFacetFilter/CollectionsFacetFilter";
import { MediaKindsFacetFilter } from "@/components/filters/MediaKindsFacetFilter/MediaKindsFacetFilter";
import { DateFacetFilter } from "@/components/filters/DateFacetFilter/DateFacetFilter";
import { SearchPageFilter } from "@/modules/filters/search/SearchPageFilter";
import { FadeHorizontalScroll } from "@/components/horizontal-scroll/FadeHorizontalScroll";
import { noop } from "lodash-es";
import { useState } from "react";
import { SortByKind } from "@/modules/lenses/types";

export const SearchPageHeader = observer(function SearchPageHeader() {
  const page = useTabPageStore("searchPage");
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const queryString = page.filters.params.queryString;

  return (
    <Wrapper>
      <MDSPanelHeader>
        <StyledHeading slot={MDSPanelHeaderSlotName.Heading}>
          <StyledSearchIcon kind={MdsIconKind.Search} />
          <MDSText variant={MDSTextVariant.HeaderXl} numberOfLines={1}>
            {queryString ? queryString : "Notes"}
          </MDSText>
        </StyledHeading>
        <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
          <MDSIconButton
            size={MDSIconButtonSize.Medium}
            iconKind={MdsIconKind.Filter}
            onClick={() => setShowFilters(!showFilters)}
            isToggled={showFilters}
          />
        </MDSPanelHeaderButtonGroup>
      </MDSPanelHeader>
      {page.isLoading && <IndeterminateLoaderBar />}
      {showFilters && (
        <WrapperBottom>
          <FiltersContainer>
            <SortDropdownButton
              isToggled={page.filters.params.sortBy !== SortByKind.LastModified}
              contentList={page.filters.sortOptions}
              label={page.filters.sortLabel}
              placement="below-right-alignment"
              dropdownIcon
            />
            {renderSortedFilters(page.filters)}
          </FiltersContainer>
        </WrapperBottom>
      )}
    </Wrapper>
  );
});

const renderSortedFilters = (pageFilters: SearchPageFilter) => {
  const filters = [
    {
      isActive: pageFilters.collectionsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFacetFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix="In"
          buttonLabel="Collections"
          selectedModeKey="in-any-of"
          onSelectMode={noop}
          noCollectionIsSelected={false}
          onNoCollectionIsSelected={noop}
          selectedItems={pageFilters.selectedCollectionTitles}
          supportModeSelection={false}
        />
      ),
    },
    {
      isActive: !!pageFilters.mediaKindsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 1,
      component: (
        <MediaKindsFacetFilter
          key="media-kinds"
          items={pageFilters.mediaKindsFacetFilterItems}
          onSelectItem={pageFilters.toggleMediaKindItem}
          onSelectClear={pageFilters.clearMediaKinds}
          selectedItems={pageFilters.selectedMediaKinds}
        />
      ),
    },
    {
      isActive: !!pageFilters.dateParams,
      defaultOrder: 2,
      component: (
        <DateFacetFilter
          key="date"
          items={pageFilters.dateFilterItems}
          selectedRange={pageFilters.dateSelectedRange}
          onClear={() => pageFilters.clearDateFilter()}
        />
      ),
    },
    {
      isActive: pageFilters.modifiedByFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedByFacetFilterItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdByFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdByFacetFilterItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div({});

const StyledSearchIcon = styled(MdsIcon)`
  .mds-icon {
    font-size: 18px;
  }
`;

const StyledHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing.md,
}));

const FiltersContainer = styled(FadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing.sm,
}));
